import { Brand, Embroidery, I18NText, Language, Model, Position, State } from 'src/types'
import { emojiChecker, positionChecker, usModelOfAtomsChecker } from 'src/logic'
import { gloveTaxIncludePrice, mittTaxIncludePrice } from 'src/features/gorilla/constants/baseModel'
import {
  glovePriceTaxIncluded,
  mittPriceTaxIncluded,
  softGloveBasePrice,
  softMittBasePrice
} from 'src/features/atoms/glove/japan/constants/baseModel'
import {
  loopOfRingFingerObjs,
  moldingJaObjs,
  printLeatherTypeObjs,
  sizeObjs
} from 'src/features/atoms/glove/japan/constants/bases'
import {
  glovePriceTaxIncluded as twGlovePriceTaxIncluded,
  mittPriceTaxIncluded as twMittPriceTaxIncluded,
  softGloveBasePrice as twSoftGloveBasePrice,
  softMittBasePrice as twSoftMittBasePrice
} from 'src/features/atoms/glove/tw/constants/baseModelPrices'
import {
  loopOfRingFingerTwObjs,
  moldingTwObjs,
  printLeatherTypeTwObjs,
  sizeTwObjs
} from 'src/features/atoms/glove/tw/constants/bases'
import { getI18NText } from 'src/locales/utils'

const getOptionPriceByValue = (objects: { value: string; price?: number }[], selectedValue?: string) => {
  if (!selectedValue || selectedValue === 'unselected') {
    return 0
  }
  return objects.find((object) => object.value === selectedValue)?.price || 0
}

export const calculatorOfBaseCost = (baseModel: Model, seriesOrKindsValue: string, language?: Language): number => {
  switch (baseModel.brand) {
    case 'atoms': {
      const isTw = language === 'zh-TW'
      const hardGlove = isTw ? twGlovePriceTaxIncluded : glovePriceTaxIncluded
      const hardMitt = isTw ? twMittPriceTaxIncluded : mittPriceTaxIncluded
      const softGlove = isTw ? twSoftGloveBasePrice : softGloveBasePrice
      const softMitt = isTw ? twSoftMittBasePrice : softMittBasePrice

      const isHardBallModel = ['hard'].includes(seriesOrKindsValue)
      const isRubberBallModel = ['soft'].includes(seriesOrKindsValue)
      const { isGlove, isMitt } = positionChecker(baseModel.position || '')
      // 軟式モデル
      if (isRubberBallModel) {
        if (isGlove) {
          return softGlove
        }
        if (isMitt) {
          return softMitt
        }
      }

      // 硬式モデル
      if (isHardBallModel) {
        if (isGlove) {
          return hardGlove
        }
        if (isMitt) {
          return hardMitt
        }
      }
      return 0
    }
    case 'yellStory': {
      switch (seriesOrKindsValue) {
        case 'newBestSelect':
          return 69300 // 税込
        case 'newGoldLineNext':
          return 66000 // 税込
        case 'newJuniorLine':
          return 55000 // 税込
        default:
          return baseModel?.price
      }
    }
    case 'gorilla': {
      // baseModelオブジェクトの値に関係なくソースコード適用時点の価格を返す
      const { isGlove, isMitt } = positionChecker(baseModel.position || '')
      if (isMitt) {
        return mittTaxIncludePrice
      }

      if (isGlove) {
        return gloveTaxIncludePrice
      }
      return 0
    }
    default: {
      return baseModel?.price || 0
    }
  }
}

export const calculatorOfOptionCost = (brand: Brand, position: Position, state: State, language?: Language): number => {
  const objectsOfLeatherColorParts = [
    // 'all',
    'catchFace', // 捕球面
    'web', // ウェブ
    'thumbHook', // 掛け紐
    'littleHook', // 掛け紐
    'indexCover', // 指カバー
    'thumbOut', // 親指外側
    'thumbWeb', // 親指内側
    'thumbIndex', // 親指・人差し指
    'thumbMachi', // キャッチャーミットの親指マチ
    'littleMachi', // キャッチャーミットの小指マチ
    'thumbWebAndIndexWeb', // Uバックの親指・人差し指
    'thumbIndexMiddleRight', // 親指・人差し指・中指右
    'indexWeb', // 人差し指右
    'indexMiddle', // 人差し指左
    'indexAndMiddle', //人差し指・中指
    'indexLeftMiddleRight', // 人差し指左・中指右
    'middleIndex', //中指右
    'middleRing', //中指左
    'middleLeftRingRight', // 中指左・薬指右
    'middleLeftRing', //  中指左・薬指
    'middleLeftRingLittle', //  中指左・薬指・小指
    'ringMiddle', // 薬指右
    'ringLittle', // 薬指左
    'ringLeftLittleRight', // 薬指左・小指右
    'littleRing', // 小指右
    'littleOut', //小指左
    'thumb', // 親指
    'boomerang', // ブーメラン
    'underWeb', // ファーストミットウェブ下
    'fingerStand', // キャッチャーミット台
    'listBelt' // ベルト
  ].filter((parts) => state[parts]?.value !== 'unselected')
  switch (brand) {
    case 'atoms': {
      const isTw = language === 'zh-TW'
      const sizeObjects = isTw ? sizeTwObjs : sizeObjs
      const moldingObjects = isTw ? moldingTwObjs : moldingJaObjs
      const loopOfRingFingerObjects = isTw ? loopOfRingFingerTwObjs : loopOfRingFingerObjs
      const printLeatherTypeObjects = isTw ? printLeatherTypeTwObjs : printLeatherTypeObjs
      const sizeChangedCost = getOptionPriceByValue(sizeObjects, state?.size?.value) // 指サイズのオプション
      const moldingOrderCost = getOptionPriceByValue(moldingObjects, state?.moldingOrder?.value)
      const loopOfRingFingerCost = getOptionPriceByValue(loopOfRingFingerObjects, state?.loopOfRingFinger?.value)
      const printLeatherTypeCost = getOptionPriceByValue(printLeatherTypeObjects, state?.printLeatherType?.value)
      const optionTotal = sizeChangedCost + moldingOrderCost + loopOfRingFingerCost + printLeatherTypeCost
      return optionTotal
    }
    case 'yellStory': {
      const sizeChangedCost = state.size.price
      const isleatherIntegratedIndex = state.leatherIntegratedIndex.value === 'atIndexFinger'
      const isleatherIntegratedMiddle = state.leatherIntegratedMiddle.value === 'atMiddleFinger'
      const isleatherIntegratedRing = state.leatherIntegratedRing.value === 'atRingFinger'
      // 革一体形状費
      const numberOfLeatherIntegrateds = [isleatherIntegratedIndex, isleatherIntegratedMiddle, isleatherIntegratedRing].filter(Boolean)?.length || 0
      const leatherIntegratedCost =
        numberOfLeatherIntegrateds === 1 ? 3300 : numberOfLeatherIntegrateds === 2 ? 4950 : numberOfLeatherIntegrateds === 3 ? 6600 : 0

      // 三色以上、革カラーを指定した時の数
      const leatherColors = objectsOfLeatherColorParts.map((x) => state[x]?.color).filter(Boolean)
      const numberOfLeatherColors = new Set(leatherColors).size
      const multiLeatherColorsCost = numberOfLeatherColors <= 2 ? 0 : 3300 * (numberOfLeatherColors - 2)
      const optionTotalCost = sizeChangedCost + leatherIntegratedCost + multiLeatherColorsCost
      return optionTotalCost
    }
    case 'dxm': {
      const loopOfRingFingerCost = state.loopOfRingFinger.value === 'loopOfRingFinger' ? state.loopOfRingFinger.price || 0 : 0
      const hamidashiCutCost = state.hamidashiCut.price || 0
      const optionTotalCost = loopOfRingFingerCost + hamidashiCutCost
      return optionTotalCost
    }
    case 'gorilla': {
      const labelCost = state.gorillaLabel?.price || 0
      const optionTotalCost = labelCost
      return optionTotalCost || 0
    }
  }
}

export const calculatorOfEmbroideryCost = (brand: Brand, t: I18NText, state: State, language?: Language): number => {
  const embroideries = state.embroideries
  const embroideriesOfFiltered = embroideries.filter((e) => e.content.trim()?.length > 0)
  const priceText = getI18NText(language || 'ja', t)
  switch (brand) {
    case 'atoms': {
      // 裏革1段目と2段目の刺繍をフィルタリング(位置のフィルタリング)
      const unitPricer = (e?: Embroidery) => {
        const isEdge = e?.edgeColor?.value !== 'none' //フチ付きかどうか
        const isShadow = e?.shadowColor?.value !== 'none' //カゲ付きかどうか
        if (e === undefined) {
          return 0
        }
        const price = isEdge ? priceText.EDGE_COLOR_PRICE : isShadow ? priceText.SHADOW_COLOR_PRICE : priceText.MAIN_COLOR_PRICE
        return price
      }
      const isUsModel = usModelOfAtomsChecker(state.baseModel?.productNumber)
      const costsOfEmbroideries = embroideriesOfFiltered.map((e) => unitPricer(e)) // [2750, 4400, ...]

      if (!isUsModel) {
        const existLiningFirst = embroideriesOfFiltered.some((e) => ['leatherLiningFirst'].includes(e.position?.value))
        const excludePrice = unitPricer(embroideriesOfFiltered.find((x) => x.position.value === 'leatherLiningFirst'))
        const discountPriceFirst = existLiningFirst ? excludePrice : 0
        const existLiningSecond = embroideriesOfFiltered.some((e) => ['leatherLiningSecond'].includes(e.position?.value))
        const excludePriceSecond = unitPricer(embroideriesOfFiltered.find((x) => x.position.value === 'leatherLiningSecond'))
        const discountPrice = existLiningSecond ? excludePriceSecond + discountPriceFirst : discountPriceFirst
        const total = costsOfEmbroideries.reduce((a, c) => a + c, 0) - discountPrice // 7150
        return total
      } else {
        // const totalUs = costsOfEmbroideries.reduce((a, c) => a + c, 0)
        // USモデルなら、絵文字の追加料金を計算してreturn
        const additionalEmojiCost = embroideriesOfFiltered.reduce((a, c) => {
          const existEmoji = emojiChecker(c.content)
          if (!existEmoji) return a
          // 絵文字が存在する↓
          const emojiCount = c.content.match(/(🩷|⭐️|♥)/g)?.length || 0
          a += priceText.MAIN_COLOR_PRICE * emojiCount
          return a
        }, 0)
        const emojiExistBetweenCharCost = embroideriesOfFiltered.reduce((a, c) => {
          const existEmoji = emojiChecker(c.content)
          if (!existEmoji) return a

          // 絵文字が存在する↓
          const separateCount = c.content.split(/(🩷|⭐️|♥)/g).filter((str) => !['🩷', '⭐️', '♥'].includes(str) && str.length > 0).length
          a += unitPricer(c) * separateCount
          return a
        }, 0)

        // MEMO 2024/07/09 にUSモデルはdiscountなしに変更
        // MEMO 2025/03/20 にUSモデルは通常料金の加算をしない方針に変更
        const usTotal = additionalEmojiCost + emojiExistBetweenCharCost // + totalUs
        return usTotal > 0 ? usTotal : 0
      }
    }
    case 'yellStory': {
      // contentが入力されているもののみ抽出
      const costsOfEmbroideries = embroideriesOfFiltered.map((e) => {
        const isEdge = e.edgeColor.value !== 'none' //フチ付きかどうか
        const isShadow = e.shadowColor.value !== 'none' //カゲ付きかどうか
        const cost = isEdge ? 4620 : isShadow ? 2970 : 2420
        return cost // 2750
      }) // [2750, 4400, ...]

      // 一番高い刺繍をサービスで無料にする
      const discountPrice = costsOfEmbroideries.length ? Math.max(...costsOfEmbroideries) : 0

      const originalEmbroideryCost = state.ysrOriginalEmbroidery?.price || 0
      const directLogo1Cost = state.ysrDirectLogo1Position?.price || 0
      const directLogo2Cost = state.ysrDirectLogo2Position?.price || 0
      const total = costsOfEmbroideries.reduce((a, c) => a + c, 0) - discountPrice + originalEmbroideryCost + directLogo1Cost + directLogo2Cost
      return total
    }
    case 'dxm': {
      const singlePrice = 2200
      const shadowPrice = 2530
      const edgePrice = 2750
      const costsOfEmbroideries = embroideriesOfFiltered
        .map((e) => {
          const isEdge = e.edgeColor.value !== 'none' //フチ付きかどうか
          const isShadow = e.shadowColor.value !== 'none' //カゲ付きかどうか
          const cost = isEdge ? edgePrice : isShadow ? shadowPrice : singlePrice
          return cost // 2750
        }) // [2750, 2200, ...]
        .reduce((a, c) => a + c, 0) //
      return costsOfEmbroideries
    }
    case 'gorilla': {
      const costsOfEmbroideries = embroideriesOfFiltered
        .map((e) => {
          const isEdge = e.edgeColor.value !== 'none' //フチ付きかどうか
          const isShadow = e.shadowColor.value !== 'none' //カゲ付きかどうか
          const cost = isEdge ? 2200 : isShadow ? 1100 : 110
          return cost // 2750
        }) // [2750, 4400, ...]
        .reduce((a, c) => a + c, 0) // 7150
      return costsOfEmbroideries
    }
  }
}
